import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { Pagination } from '@/shared/components/custom/pagination/Pagination';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs';
import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/pagination';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { useForms } from '../hooks/useForms';
import { useTemplates } from '../hooks/useTemplates';
import { useAssignedForms } from '../hooks/useAssignedForms';
import { FormsListFilters } from '../components/FormsList/FormsListFilters';
import { FormsListTable } from '../components/FormsList/FormsListTable';
import { AssignedFormsTable } from '../components/FormsList/AssignedFormsTable';
import { CreateFormMenu } from '../components/CreateFormMenu';
import type { MergedListItem } from '../adapters/formColumns';
import type { FormsFilters } from '../types';

export default function FormsListPage() {
  const { t } = useTranslation('forms');
  const [filters, setFilters] = useState<FormsFilters>({});

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);

  const page = pageIndex + 1;
  const { forms, pageInfo } = useForms(filters, page, pageSize);

  /**
   * Templates have no `FormStatus`/project scope, so they're excluded whenever
   * a status or scope filter is active — they'd never match and would misrepresent
   * the filtered count.
   */
  const includeTemplates = !filters.status && !filters.projectId;
  const { templates, pageInfo: templatesPageInfo } = useTemplates(
    { searchTerm: filters.searchTerm },
    page,
    pageSize,
    includeTemplates,
  );

  /**
   * Forms and templates are separate paginated backend endpoints (no unified
   * list endpoint exists) — merged client-side per page (same page/pageSize
   * requested from both), sorted by `updatedAt`, then trimmed back down to one
   * page's worth. Documented approximation: near a page boundary an item can
   * shift between pages if one source has disproportionately more recent
   * activity than the other in that window. A true merged cursor would need a
   * dedicated backend endpoint — logged in forms-backend-requests.md.
   */
  const items: MergedListItem[] = [
    ...forms.map((item): MergedListItem => ({ itemType: 'form', item })),
    ...(includeTemplates
      ? templates.map((item): MergedListItem => ({ itemType: 'template', item }))
      : []),
  ]
    .sort((a, b) => b.item.updatedAt.localeCompare(a.item.updatedAt))
    .slice(0, pageSize);

  const combinedTotal =
    (pageInfo?.totalCount ?? 0) + (includeTemplates ? (templatesPageInfo?.totalCount ?? 0) : 0);
  if (combinedTotal !== totalCount) {
    setTotalCount(combinedTotal);
  }

  const pagination = usePagination({
    totalItems: totalCount,
    pageIndex,
    onPageChange: setPageIndex,
    pageSize,
    onPageSizeChange: setPageSize,
  });

  const [assignedPageIndex, setAssignedPageIndex] = useState(0);
  const [assignedPageSize, setAssignedPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [assignedTotalCount, setAssignedTotalCount] = useState(0);

  const { forms: assignedForms, pageInfo: assignedPageInfo } = useAssignedForms(
    assignedPageIndex + 1,
    assignedPageSize,
  );

  if (assignedPageInfo && assignedPageInfo.totalCount !== assignedTotalCount) {
    setAssignedTotalCount(assignedPageInfo.totalCount);
  }

  const assignedPagination = usePagination({
    totalItems: assignedTotalCount,
    pageIndex: assignedPageIndex,
    onPageChange: setAssignedPageIndex,
    pageSize: assignedPageSize,
    onPageSizeChange: setAssignedPageSize,
  });

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div className="p-4 md:p-6 space-y-4" data-testid="forms-list-page">
        <Toolbar>
          <Toolbar.Info title={t('list.title')} description={t('list.subtitle')} />
          <Toolbar.Actions>
            <FormsListFilters
              filters={filters}
              onChange={(next) => {
                setFilters(next);
                setPageIndex(0);
              }}
            />
            <CreateFormMenu />
          </Toolbar.Actions>
        </Toolbar>

        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all">{t('tabs.allForms')}</TabsTrigger>
            <TabsTrigger value="assigned">{t('tabs.assignedToMe')}</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4 space-y-4">
            <FormsListTable items={items} />
            {totalCount > 0 && <Pagination {...pagination} />}
          </TabsContent>

          <TabsContent value="assigned" className="mt-4 space-y-4">
            <AssignedFormsTable forms={assignedForms} />
            {assignedTotalCount > 0 && <Pagination {...assignedPagination} />}
          </TabsContent>
        </Tabs>
      </div>
    </FeatureErrorBoundary>
  );
}
